import React, { useMemo, useCallback, useEffect, forwardRef } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import InputAdornment from '@material-ui/core/InputAdornment'
import SearchIcon from '@material-ui/icons/Search'

import cultivarSchema from '@galo-monitor/forms/schemas/root/cultivar.schema'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { CultivarActions } from '@galo-monitor/stores/cultivar'
import colors from '@galo-monitor/styles/colors'
import Form from '@galo-monitor/web-components/Form'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'

import { Container } from './theme'

const CultivarForm = forwardRef((props, formRef) => {
  const { cultivar, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const { data: cultivarData, isValidating: isValidatingCultivarData } = useGaloMonitorApi(cultivar.id ? `/admin/cultivars/${ cultivar.id }` : null)
  const { data: breeders } = useGaloMonitorApi('/admin/breeders')
  const { data: cultures } = useGaloMonitorApi('/admin/cultures?country=1')

  const breedersOptions = useMemo(
    () => breeders?.length > 0
      ? map(breeders,
        ({ name }) => ({
          label: name,
          value: name
        })
      )
      : [],
    [breeders]
  )

  const culturesOptions = useMemo(
    () => cultures?.length > 0
      ? map(cultures,
        ({ name, id }) => ({
          label: name,
          value: id.toString()
        })
      )
      : [],
    [cultures]
  )

  const handleSubmit = useCallback(
    data => {
      setLoading(true)
      dispatch(CultivarActions.saveCultivar(
        {
          ...cultivar,
          ...data
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('cultivar', { howMany: 1 }),
            action: toLower(t(isEmpty(cultivar) ? 'created' : 'updated', { gender: 'female', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [cultivar, dispatch, onError, onSuccess, setLoading, snackbar, t]
  )

  useEffect(() => {
    if (!isEmpty(cultivarData))
      setLoading(false)
  }, [cultivarData, isValidatingCultivarData, setLoading])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => cultivar.id && setLoading(true), [])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ cultivarSchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <InputText
          name="name"
          label={ t('name') }
          defaultValue={ cultivar?.name || '' }
          disabled={ loading }
          fullWidth
        />
        <InputSelect
          name="culture_id"
          label={ t('select the {this}', {
            this: t('culture', { howMany: 1 }),
            gender: 'female',
            howMany: 1
          }) }
          options={ culturesOptions }
          disabled={ loading }
          defaultValue={ cultivarData?.cultureId.toString() ?? '' }
          disableClearable
          fullWidth
        />

        <InputSelect
          name="breeder_name"
          label={ t('select the {this}', {
            this: t('breeder', { howMany: 1 }),
            gender: 'female',
            howMany: 1
          }) }
          options={ breedersOptions }
          disabled={ loading }
          InputProps={ {
            endAdornment: (
              <InputAdornment position="end" style={ { marginRight: -25 } }>
                <SearchIcon style={ { color: colors.lightGrey4 } } />
              </InputAdornment>
            )
          } }
          defaultValue={ cultivarData?.breeder.name ?? '' }
          creatable
          disableClearable
          fullWidth
        />
      </Container>
    </Form>
  )
})

CultivarForm.propTypes = {
  cultivar: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

CultivarForm.defaultProps = {
  cultivar: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default CultivarForm
